import { Button } from '@/components/ui/button';
import { fetchMusicDataDirect } from '@/lib/supabase';
import type { MusicData } from '@/types/music';
import { Copy, Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

// Types
export interface ExportButtonProps {
  selectedDate: string;
  onExport: (date: string) => void;
  disabled?: boolean;
}

interface ExportOptions {
  format: 'copy' | 'download';
}

// Custom hook for playlist data
const usePlaylistData = (selectedDate: string) => {
  const [isLoading, setIsLoading] = useState(false);

  const fetchPlaylistData = async () => {
    if (!selectedDate) {
      toast.error('Please select a date first');
      return null;
    }

    setIsLoading(true);

    try {
      const result = await fetchMusicDataDirect(0, 100, selectedDate);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch music data');
      }

      if (!result.data || result.data.length === 0) {
        toast.info('No music data found for the selected date');
        return null;
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(error instanceof Error ? error.message : 'An unexpected error occurred');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, fetchPlaylistData };
};

// Utility functions
const formatPlaylistText = (data: MusicData[]) => {
  return data
    .map((item: MusicData, index: number) => `${index + 1}. ${item.title} - ${item.artist}`)
    .join('\n');
};

const copyToClipboard = async (text: string): Promise<boolean> => {
  // Try modern clipboard API
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.error('Clipboard API error:', err);
    }
  }

  // Fallback for iOS and other devices with limited clipboard support
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    // Make the textarea out of viewport
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);

    if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
      // iOS specific workaround
      textArea.contentEditable = 'true';
      textArea.readOnly = false;

      const range = document.createRange();
      range.selectNodeContents(textArea);

      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        textArea.setSelectionRange(0, 999999);
      }
    } else {
      textArea.select();
    }

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    console.error('Fallback clipboard error:', err);
    return false;
  }
};

const downloadPlaylist = (text: string, date: string): void => {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `playlist-${date}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Main component
const ExportButton = ({ selectedDate, onExport, disabled }: ExportButtonProps) => {
  const { isLoading, fetchPlaylistData } = usePlaylistData(selectedDate);

  const handleExport = async ({ format }: ExportOptions) => {
    const data = await fetchPlaylistData();
    if (!data) return;

    const playlistText = formatPlaylistText(data);
    let success = false;

    try {
      switch (format) {
        case 'copy':
          success = await copyToClipboard(playlistText);
          if (success) {
            toast.success('Playlist copied to clipboard!');
          } else {
            toast.error("Couldn't copy to clipboard. Try the download option instead.");
          }
          break;

        case 'download':
          downloadPlaylist(playlistText, selectedDate);
          toast.success('Playlist downloaded successfully!');
          success = true;
          break;
      }

      if (success) {
        onExport(selectedDate);
      }
    } catch (error) {
      console.error('Error during export:', error);
      toast.error(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  };

  const buttonBaseClass =
    'transition-all duration-200 ease-in-out hover:bg-gray-100 dark:hover:bg-gray-800 hover:scale-105 hover:shadow-sm border-gray-200 dark:border-gray-700';

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className={buttonBaseClass}
        onClick={() => handleExport({ format: 'copy' })}
        disabled={isLoading || disabled}
      >
        {isLoading ? (
          <Loader2 className="h-5 w-5 md:h-4 md:w-4 md:mr-1.5 animate-spin text-gray-600 dark:text-gray-300" />
        ) : (
          <Copy className="h-5 w-5 md:h-4 md:w-4 md:mr-1.5 text-gray-600 dark:text-gray-300" />
        )}
        <span className="hidden md:inline font-medium text-gray-700 dark:text-gray-200">Copy</span>
      </Button>

      <Button
        variant="outline"
        size="sm"
        className={buttonBaseClass}
        onClick={() => handleExport({ format: 'download' })}
        disabled={isLoading || disabled}
      >
        <Download className="h-5 w-5 md:h-4 md:w-4 md:mr-1.5 text-gray-600 dark:text-gray-300" />
        <span className="hidden md:inline font-medium text-gray-700 dark:text-gray-200">
          Download
        </span>
      </Button>
    </>
  );
};

export default ExportButton;
